import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import { useWebSocket } from '../WebSocketContext';
import './JourneySearchModal.css';

const apiUrl = process.env.REACT_APP_API_URL;

// Options from packit creation
const goalOptions = [
  'Relaxation', 'Adventure', 'Learning', 'Connection', 'Escape', 'Celebration'
];

const desireOptions = [
  'Beach', 'Mountains', 'City', 'Countryside', 'Culture', 'Food', 'History', 'Nature', 'Outdoors', 'Exploration'
];

const feelingOptions = [
  'Excited', 'Peaceful', 'Energetic', 'Curious', 'Nostalgic', 'Inspired', 'Adventurous', 'Cozy'
];

const seasonOptions = [
  'Spring', 'Summer', 'Fall', 'Winter'
];

const scopeLevelOptions = [
  { value: 'Global', label: 'Global' },
  { value: 'Country', label: 'Country' },
  { value: 'State', label: 'State' }
];

const ownerOptions = [
  { value: 'System', label: 'Ideal Scribe System' },
  { value: 'Community', label: 'Community Generated' }
];

const sortOptions = [
  { value: 'newest', label: 'Newest First' },
  { value: 'rating', label: 'Highest Rated' }
];

// Helper function to get user ID from token
const getUserFromToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub;
  } catch (error) {
    console.error('Error parsing JWT token:', error);
    return null;
  }
};

const JourneySearchModal = ({ isOpen, onClose, onJourneySelect, userPlan = 'Basic' }) => {
  const { connectionId } = useWebSocket(); // Get connectionId for journey-progress API calls
  const [currentUserId, setCurrentUserId] = useState(null);
  const [journeyOwnership, setJourneyOwnership] = useState({}); // Map of journeyID -> { ownerID, isPublic }
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    ownerID: '',
    scopeLevel: '',
    country: '',
    state: '',
    desires: [],
    feelings: [],
    goals: [],
    recommendedSeason: [],
    sortBy: 'newest',
    locationCountMin: '',
    locationCountMax: ''
  });
  
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [journeyDetails, setJourneyDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isAddingJourney, setIsAddingJourney] = useState(false);
  const [addingJourneyId, setAddingJourneyId] = useState(null); // Track which journey is being added
  const [showDetailsView, setShowDetailsView] = useState(false);
  const [journeysToCompare, setJourneysToCompare] = useState([]);
  const [showReviews, setShowReviews] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState({
    desires: false,
    feelings: false,
    goals: false,
    season: false
  });

  // Questionnaire state
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [questionnaireStep, setQuestionnaireStep] = useState(0);
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState({
    journeyType: '', // 'goal', 'desire', 'feeling'
    journeyTypeValue: '', // specific goal/desire/feeling
    isFamily: null, // null = not answered, true/false = answered
    specialNeeds: '',
    activity: '', // 'hiking', 'biking', 'fishing', 'skiing', 'none'
    accessibility: '', // 'local', 'any'
    locationCount: '', // 'small' (5-50), 'medium' (51-200), 'large' (201+), 'any'
    season: '' // 'spring', 'summer', 'fall', 'winter', 'any'
  });

  // Track if this is the initial load to avoid duplicate API calls
  const isInitialLoadRef = useRef(true);

  // Fetch current user ID when modal opens
  useEffect(() => {
    const fetchCurrentUserId = async () => {
      try {
        const token = (await fetchAuthSession()).tokens?.idToken?.toString();
        const userId = getUserFromToken(token);
        setCurrentUserId(userId);
        // Don't set ownerID - default to "All Owners"
      } catch (error) {
        console.error('Error fetching current user ID:', error);
      }
    };

    if (isOpen) {
      fetchCurrentUserId();
    }
  }, [isOpen]);

  // Fetch journey ownership and public status for search results
  useEffect(() => {
    const fetchJourneyOwnership = async () => {
      if (!currentUserId || searchResults.length === 0) return;

      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };

      // Fetch ownership for journeys that don't have it cached
      const journeysToFetch = searchResults.filter(j => {
        const journeyId = j.journeyID || j.id;
        return journeyId && !journeyOwnership[journeyId];
      });

      if (journeysToFetch.length === 0) return;

      try {
        const ownershipPromises = journeysToFetch.map(async (journey) => {
          const journeyId = journey.journeyID || journey.id;
          try {
            // Use dataType from journey if available, otherwise don't specify (let API default)
            const dataType = journey.dataType ? `?dataType=${journey.dataType}` : '';
            const response = await axios.get(`${apiUrl}/journeys/${journeyId}${dataType}`, { headers });
            const journeyData = response.data;
            // Properly handle public field - can be boolean or string
            // Convert to boolean: explicitly check for false (boolean or string), default to true
            let isPublic = true; // Default to true
            const publicValue = journeyData.public;
            
            // Debug logging
            if (process.env.NODE_ENV === 'development') {
              console.log(`[JourneyDelete] Journey ${journeyId}: public=${publicValue}, type=${typeof publicValue}`);
            }
            
            if (publicValue === false || publicValue === 'false') {
              isPublic = false;
            } else if (publicValue === true || publicValue === 'true') {
              isPublic = true;
            }
            // If undefined/null, keep default of true
            
            if (process.env.NODE_ENV === 'development') {
              console.log(`[JourneyDelete] Journey ${journeyId}: isPublic=${isPublic}`);
            }
            
            return {
              journeyId,
              ownerID: journeyData.ownerID || 'System',
              isPublic: isPublic
            };
          } catch (error) {
            console.warn(`Could not fetch ownership for journey ${journeyId}:`, error);
            return {
              journeyId,
              ownerID: 'System',
              isPublic: true
            };
          }
        });

        const ownershipData = await Promise.all(ownershipPromises);
        const newOwnership = {};
        ownershipData.forEach(data => {
          if (data) {
            newOwnership[data.journeyId] = {
              ownerID: data.ownerID,
              isPublic: data.isPublic
            };
          }
        });

        if (Object.keys(newOwnership).length > 0) {
          setJourneyOwnership(prev => ({ ...prev, ...newOwnership }));
        }
      } catch (error) {
        console.error('Error fetching journey ownership:', error);
      }
    };

    fetchJourneyOwnership();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, currentUserId]);

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setCurrentPage(1);
      setLastEvaluatedKey(null);
      setHasMorePages(false);
      setSelectedJourney(null);
      setJourneyDetails(null);
      setShowDetailsView(false);
      setIsAddingJourney(false);
      setAddingJourneyId(null);
      setShowQuestionnaire(false);
      setQuestionnaireStep(0);
      setQuestionnaireAnswers({
        journeyType: '',
        journeyTypeValue: '',
        isFamily: null,
        specialNeeds: '',
        activity: '',
        accessibility: '',
        locationCount: '',
        season: ''
      });
      // Mark as initial load when modal opens
      isInitialLoadRef.current = true;
      setSearchParams(prev => ({
        ...prev,
        keyword: '',
        ownerID: '', // Default to "All Owners"
        scopeLevel: '',
        country: '',
        state: '',
        desires: [],
        feelings: [],
        goals: [],
        recommendedSeason: [],
        sortBy: 'newest',
        locationCountMin: '',
        locationCountMax: ''
      }));
      // Add class to body to hide map elements
      document.body.classList.add('journey-modal-open');
    } else {
      // Remove class from body
      document.body.classList.remove('journey-modal-open');
      // Reset initial load flag when modal closes
      isInitialLoadRef.current = false;
      // Clear journey ownership cache when modal closes to prevent memory accumulation
      setJourneyOwnership({});
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('journey-modal-open');
    };
  }, [isOpen, currentUserId]);

  // Auto-search when filters change or modal opens
  useEffect(() => {
    if (isOpen) {
      // On initial load, don't debounce - call immediately
      // On filter changes, debounce by 300ms
      const debounceDelay = isInitialLoadRef.current ? 0 : 300;
      
      const timeoutId = setTimeout(() => {
        // Clear old results immediately when filters change
        setSearchResults([]);
        setCurrentPage(1);
        setLastEvaluatedKey(null);
        setHasMorePages(false);
        // Always pass null for explicitKey when filters/sort change (not loading more)
        // This ensures no lastEvaluatedKey is used in the query
        searchJourneys(1, false, null);
        // Mark initial load as complete after first call
        isInitialLoadRef.current = false;
      }, debounceDelay);
      
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isOpen]);

  const buildQueryParams = (keyToUse = null) => {
    const params = new URLSearchParams();
    params.append('limit', '10');
    
    if (searchParams.keyword) {
      params.append('keyword', searchParams.keyword);
    }
    if (searchParams.ownerID) {
      params.append('ownerID', searchParams.ownerID);
    }
    if (searchParams.scopeLevel) {
      params.append('scopeLevel', searchParams.scopeLevel);
    }
    if (searchParams.country) {
      params.append('country', searchParams.country);
    }
    if (searchParams.state) {
      params.append('state', searchParams.state);
    }
    if (searchParams.desires.length > 0) {
      params.append('desires', searchParams.desires.join(','));
    }
    if (searchParams.feelings.length > 0) {
      params.append('feelings', searchParams.feelings.join(','));
    }
    if (searchParams.goals.length > 0) {
      params.append('goals', searchParams.goals.join(','));
    }
    if (searchParams.recommendedSeason.length > 0) {
      params.append('recommendedSeason', searchParams.recommendedSeason.join(','));
    }
    if (searchParams.locationCountMin) {
      params.append('locationCountMin', searchParams.locationCountMin);
    }
    if (searchParams.locationCountMax) {
      params.append('locationCountMax', searchParams.locationCountMax);
    }
    if (searchParams.sortBy) {
      params.append('sortBy', searchParams.sortBy);
    }
    // Only include lastEvaluatedKey if explicitly provided (for load more)
    // When sortBy or filters change, keyToUse will be null and we start fresh
    if (keyToUse !== null) {
      // lastEvaluatedKey from API is already URL encoded, use as-is
      // It comes in format like: %7B%22ownerID%22%3A%20%22System%22%2C%20%22journeyID%22%3A%20%22jr-xxx%22%7D
      params.append('lastEvaluatedKey', keyToUse);
    }
    
    return params.toString();
  };

  const searchJourneys = async (page = 1, useLastKey = false, explicitKey = null) => {
    setIsLoading(true);
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };
      
      // Priority: explicitKey (passed directly) > lastEvaluatedKey from state (if useLastKey) > null
      // When loading more, explicitly pass the current lastEvaluatedKey to avoid React closure issues
      const keyToUse = explicitKey !== null ? explicitKey : (useLastKey ? lastEvaluatedKey : null);
      
      const queryString = buildQueryParams(keyToUse);
      const response = await axios.get(`${apiUrl}/journeys?${queryString}`, { headers });
      const data = response.data;
      
      if (data.items) {
        // Always replace results on page 1 (new search/filter), append on subsequent pages
        if (page === 1) {
          setSearchResults(data.items);
        } else {
          // Deduplicate results when appending - filter out journeys that already exist
          setSearchResults(prev => {
            const existingIds = new Set(prev.map(j => j.journeyID));
            const newItems = data.items.filter(j => !existingIds.has(j.journeyID));
            return [...prev, ...newItems];
          });
        }
        // Update pagination state - capture lastEvaluatedKey from API response
        // The API returns lastEvaluatedKey as a URL-encoded string (e.g., %7B%22ownerID%22...)
        // Check multiple possible field names (case variations)
        const newLastKey = data.lastEvaluatedKey || data.LastEvaluatedKey || data.lastEvaluatedkey || null;
        
        // Check if there are more pages: if we have a lastEvaluatedKey OR if we got exactly the limit (10 items)
        const hasMore = !!newLastKey || data.items.length === 10;
        setLastEvaluatedKey(newLastKey);
        setHasMorePages(hasMore);
        setCurrentPage(page);
      } else {
        // No items returned
        if (page === 1) {
          setSearchResults([]);
        }
        setHasMorePages(false);
        setLastEvaluatedKey(null);
      }
    } catch (error) {
      console.error('Error searching journeys:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchJourneyDetails = async (journeyID, journey = null) => {
    setIsLoadingDetails(true);
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      // Use dataType from journey if available, otherwise default to geojson
      const dataType = journey?.dataType || journeyDetails?.dataType || 'geojson';
      const response = await axios.get(`${apiUrl}/journeys/${journeyID}?dataType=${dataType}`, { 
        headers,
        withCredentials: false // Explicitly set CORS behavior
      });
      setJourneyDetails(response.data);
    } catch (error) {
      console.error('Error fetching journey details:', error);
      
      // Provide more detailed error information
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        
        if (error.response.status === 401) {
          console.error('Authentication failed. Token may be expired or invalid.');
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up request:', error.message);
      }
      
      // Show user-friendly error message
      alert('Failed to load journey details. Please try again or contact support if the issue persists.');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setLastEvaluatedKey(null);
    // Explicitly pass null to ensure no lastEvaluatedKey is used
    searchJourneys(1, false, null);
  };

  const handleLoadMore = async () => {
    if (isLoading || !hasMorePages) {
      return;
    }
    
    // Call searchJourneys with next page and explicitly pass the lastEvaluatedKey
    await searchJourneys(currentPage + 1, true, lastEvaluatedKey);
  };

  const handleJourneyClick = async (journey) => {
    setSelectedJourney(journey);
    setShowDetailsView(true);
    await fetchJourneyDetails(journey.journeyID, journey);
  };

  const handleBackToSearch = () => {
    setShowDetailsView(false);
    setSelectedJourney(null);
    setJourneyDetails(null);
  };

  const handleAddJourney = async () => {
    if (!journeyDetails || isAddingJourney) return;
    
    try {
      setIsAddingJourney(true);
      
      // Add journey to user via PATCH
      await addJourneyToUser(journeyDetails.journeyID, journeyDetails);
      
      // Call the original handler if provided
      if (onJourneySelect) {
        onJourneySelect(journeyDetails);
      }
      
      onClose();
    } catch (error) {
      console.error('Error adding journey:', error);
      const errorMessage = error.message || 'Failed to add journey. Please try again.';
      alert(errorMessage);
    } finally {
      setIsAddingJourney(false);
    }
  };

  // Parse GeoJSON features and extract properties
  const getGeoJSONFeatures = (journey = journeyDetails) => {
    if (!journey?.geospatialBoundaries) return [];
    
    const geoData = journey.geospatialBoundaries;
    
    // Handle GeoJSON FeatureCollection format
    if (geoData && geoData.type === 'FeatureCollection' && Array.isArray(geoData.features)) {
      return geoData.features;
    }
    
    // Handle object-based format (like boundingbox dataType)
    if (geoData && typeof geoData === 'object' && !Array.isArray(geoData) && !geoData.type) {
      // Convert object format to array of location objects
      return Object.keys(geoData).map(locationName => {
        const locationData = geoData[locationName];
        return {
          name: locationName,
          properties: locationData.properties || {
            name: locationName,
            ...locationData
          },
          bounds: locationData.bounds || null
        };
      });
    }
    
    return [];
  };

  // Generate random RGB color for journey
  const generateRandomJourneyColor = () => {
    return {
      r: Math.floor(Math.random() * 256),
      g: Math.floor(Math.random() * 256),
      b: Math.floor(Math.random() * 256)
    };
  };

  // Get user ID from token
  const getUserFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return null;
    }
  };

  // Add journey to user via PATCH
  const addJourneyToUser = async (journeyID, journey = null) => {
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const user = getUserFromToken(token);

      if (!user) {
        throw new Error('Failed to retrieve user from JWT token.');
      }

      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };

      // Fetch current user data to get existing journeys
      const url = `${apiUrl}/users/${encodeURIComponent(user)}`;
      const getUserResponse = await axios.get(url, { headers });
      const currentUserData = getUserResponse.data[0];
      const existingJourneys = currentUserData?.journeys || [];

      // Check if journey already exists
      const existingJourneyIndex = existingJourneys.findIndex(
        j => j.elementID === journeyID
      );

      // Check journey limit (10 active journeys max - completed journeys don't count)
      const MAX_ACTIVE_JOURNEYS = 10;
      // Filter to only count active (non-completed) journeys
      const activeJourneys = existingJourneys.filter(j => !j.isComplete);
      if (existingJourneyIndex < 0 && activeJourneys.length >= MAX_ACTIVE_JOURNEYS) {
        throw new Error(`Active journey limit reached. You can only have up to ${MAX_ACTIVE_JOURNEYS} active journeys. Complete existing journeys to add more.`);
      }

      // Check current counts for displayJourney and isActive
      const currentlyDisplayed = existingJourneys.filter(j => j.displayJourney === true).length;
      const currentlyActive = existingJourneys.filter(j => j.isActive === true).length;
      
      // Determine if new journey should be displayed/active (max 3 each)
      // If at limit, set to false; otherwise set to true
      const shouldDisplay = currentlyDisplayed < 3;
      const shouldBeActive = currentlyActive < 3;
      
      const journeyColor = generateRandomJourneyColor();
      const newJourney = {
        elementID: journeyID,
        journeyColor: journeyColor,
        displayItemsMode: "Both",
        overrides: [],
        isActive: shouldBeActive,
        manualPercentComplete: 0.0,
        displayJourney: shouldDisplay,
        isComplete: false,
        isFavorite: false
      };
      
      // If we're at the limit, show a message to the user
      if (!shouldDisplay || !shouldBeActive) {
        const messages = [];
        if (!shouldDisplay) {
          messages.push('display');
        }
        if (!shouldBeActive) {
          messages.push('active');
        }
        const message = `Maximum of 3 journeys can be ${messages.join(' and ')} at once. This journey has been added but is not ${messages.join(' or ')}. You can enable it by disabling another journey.`;
        setTimeout(() => alert(message), 500); // Delay to avoid blocking the add operation
      }

      let updatedJourneys;
      if (existingJourneyIndex >= 0) {
        // Update existing journey - check limits before setting displayJourney/isActive
        const existingJourney = existingJourneys[existingJourneyIndex];
        const wasDisplayed = existingJourney.displayJourney === true;
        const wasActive = existingJourney.isActive === true;
        
        // Count others (excluding the one being updated)
        const othersDisplayed = existingJourneys.filter((j, idx) => idx !== existingJourneyIndex && j.displayJourney === true).length;
        const othersActive = existingJourneys.filter((j, idx) => idx !== existingJourneyIndex && j.isActive === true).length;
        
        // Only allow setting to true if under limit
        const canDisplay = shouldDisplay || (wasDisplayed && othersDisplayed < 3);
        const canBeActive = shouldBeActive || (wasActive && othersActive < 3);
        
        updatedJourneys = [...existingJourneys];
        updatedJourneys[existingJourneyIndex] = {
          ...newJourney,
          displayJourney: canDisplay,
          isActive: canBeActive
        };
        
        // Show message if limits prevented setting to true
        if (!canDisplay || !canBeActive) {
          const messages = [];
          if (!canDisplay && shouldDisplay) {
            messages.push('display');
          }
          if (!canBeActive && shouldBeActive) {
            messages.push('active');
          }
          if (messages.length > 0) {
            const message = `Maximum of 3 journeys can be ${messages.join(' and ')} at once. Please disable another journey first.`;
            setTimeout(() => alert(message), 500);
          }
        }
      } else {
        // Add new journey
        updatedJourneys = [...existingJourneys, newJourney];
      }

      // Create PATCH payload with updated journeys array
      const patchData = {
        journeys: updatedJourneys
      };

      await axios.patch(url, patchData, { headers });
      
      // Trigger journey progress recalculation after adding journey
      // This will update journey progress via WebSocket
      try {
        // Get connectionId from WebSocket context
        const wsConnectionId = connectionId || '';
        
        // Fetch the journey details to get ownerID for the newly added journey
        let journeyOwnerID = 'System'; // Default to System
        try {
          // Use dataType from journey parameter or journeyDetails state if available, otherwise don't specify
          const journeyForDataType = journey || journeyDetails;
          const dataType = journeyForDataType?.dataType ? `?dataType=${journeyForDataType.dataType}` : '';
          const journeyResponse = await axios.get(`${apiUrl}/journeys/${journeyID}${dataType}`, { headers });
          const journeyData = journeyResponse.data;
          journeyOwnerID = journeyData.ownerID || 'System';
        } catch (journeyFetchError) {
          console.warn('Could not fetch journey details for ownerID, defaulting to System:', journeyFetchError);
        }
        
        // Build POST body with all journeys including the newly added one
        // We need ownerID for each journey - fetch journey details for each journey to get ownerID
        const journeysWithOwnerID = await Promise.all(
          updatedJourneys.map(async (j) => {
            try {
              // Use dataType from journey if available, otherwise don't specify
              const dataType = j.dataType ? `?dataType=${j.dataType}` : '';
              const journeyResponse = await axios.get(`${apiUrl}/journeys/${j.elementID}${dataType}`, { headers });
              const journeyData = journeyResponse.data;
              return {
                journeyID: j.elementID,
                ownerID: journeyData.ownerID || 'System'
              };
            } catch (error) {
              // If fetch fails, use System as default
              console.warn(`Could not fetch ownerID for journey ${j.elementID}, defaulting to System:`, error);
              return {
                journeyID: j.elementID,
                ownerID: 'System'
              };
            }
          })
        );
        
        const postBody = {
          journeys: journeysWithOwnerID.filter(j => j.journeyID), // Only include journeys with journeyID
          clientId: wsConnectionId
        };
        
        // Trigger recalculation asynchronously (non-blocking)
        axios.post(
          `${apiUrl}/users/${encodeURIComponent(user)}/journey-progress?sendWebSocket=true`,
          postBody,
          { headers }
        ).then(response => {
          if (response.status === 201) {
            console.log('Journey progress recalculation triggered after adding journey (201)', response.data);
          } else {
            console.log('Journey progress recalculation triggered (status:', response.status, ')', response.data);
          }
        }).catch(progressError => {
          // Don't fail journey addition if progress recalculation fails
          console.warn('Failed to trigger journey progress recalculation after adding journey:', progressError);
        });
      } catch (progressError) {
        // Don't fail journey addition if progress recalculation setup fails
        console.warn('Error setting up journey progress recalculation:', progressError);
      }
      
      return true;
    } catch (error) {
      console.error('Error adding journey to user:', error);
      throw error;
    }
  };

  const handleQuickAdd = async (journey) => {
    if (addingJourneyId === journey.journeyID) return; // Prevent duplicate adds
    
    try {
      setAddingJourneyId(journey.journeyID);
      
      // Fetch full details if not already loaded
      let journeyToAdd = journey;
      if (!journey.geospatialBoundaries) {
        const token = (await fetchAuthSession()).tokens?.idToken?.toString();
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        };
        
        // Use dataType from journey if available, otherwise default to geojson
        const dataType = journey.dataType || 'geojson';
        const response = await axios.get(`${apiUrl}/journeys/${journey.journeyID}?dataType=${dataType}`, { headers });
        journeyToAdd = response.data;
      }

      // Add journey to user via PATCH
      await addJourneyToUser(journeyToAdd.journeyID, journeyToAdd);
      
      // Call the original handler if provided
      if (onJourneySelect) {
        onJourneySelect(journeyToAdd);
      }
      
      onClose();
    } catch (error) {
      console.error('Error adding journey:', error);
      const errorMessage = error.message || 'Failed to add journey. Please try again.';
      alert(errorMessage);
    } finally {
      setAddingJourneyId(null);
    }
  };

  const handleToggleCompare = (journey) => {
    setJourneysToCompare(prev => {
      const exists = prev.find(j => j.journeyID === journey.journeyID);
      if (exists) {
        return prev.filter(j => j.journeyID !== journey.journeyID);
      } else {
        return [...prev, journey];
      }
    });
  };

  const handleDeleteJourney = async (e, journey) => {
    e.stopPropagation();
    
    const journeyId = journey.journeyID || journey.id;
    let ownershipData = journeyOwnership[journeyId];
    
    // First, try to use journey data from search results if available
    if (!ownershipData && journey) {
      // Check if journey object has public and ownerID fields
      const journeyPublic = journey.public;
      const journeyOwnerID = journey.ownerID;
      
      if (journeyOwnerID !== undefined || journeyPublic !== undefined) {
        // Properly handle public field from journey object
        let isPublic = true; // Default to true
        if (journeyPublic === false || journeyPublic === 'false') {
          isPublic = false;
        } else if (journeyPublic === true || journeyPublic === 'true') {
          isPublic = true;
        }
        
        ownershipData = {
          ownerID: journeyOwnerID || 'System',
          isPublic: isPublic
        };
        
        // Cache it for future use
        setJourneyOwnership(prev => ({
          ...prev,
          [journeyId]: ownershipData
        }));
      }
    }
    
    // If still no ownership data, fetch it directly from API
    if (!ownershipData) {
      try {
        const token = (await fetchAuthSession()).tokens?.idToken?.toString();
        const headers = {
          'Content-type': 'application/json; charset=UTF-8',
          'Authorization': `Bearer ${token}`
        };
        // Use dataType from journey if available, otherwise don't specify
        const dataType = journey?.dataType ? `?dataType=${journey.dataType}` : '';
        const response = await axios.get(`${apiUrl}/journeys/${journeyId}${dataType}`, { headers });
        const journeyData = response.data;
        
        // Properly handle public field - can be boolean or string
        let isPublic = true; // Default to true
        const publicValue = journeyData.public;
        if (publicValue === false || publicValue === 'false') {
          isPublic = false;
        } else if (publicValue === true || publicValue === 'true') {
          isPublic = true;
        }
        
        ownershipData = {
          ownerID: journeyData.ownerID || 'System',
          isPublic: isPublic
        };
        
        // Cache it for future use
        setJourneyOwnership(prev => ({
          ...prev,
          [journeyId]: ownershipData
        }));
      } catch (error) {
        console.error('Error fetching journey for deletion:', error);
        alert('Failed to load journey information. Please try again.');
        return;
      }
    }

    // Check if user owns the journey
    if (ownershipData.ownerID !== currentUserId) {
      alert('You can only delete journeys that you own.');
      return;
    }

    // Check if journey is public - use explicit false check
    // Only block if explicitly public (true or 'true'), allow deletion if false, 'false', or undefined
    const isPublic = ownershipData.isPublic === true || ownershipData.isPublic === 'true';
    
    // Debug logging
    if (process.env.NODE_ENV === 'development') {
      console.log(`[JourneyDelete] Attempting to delete journey ${journeyId}:`, {
        ownerID: ownershipData.ownerID,
        currentUserId: currentUserId,
        isPublic: ownershipData.isPublic,
        isPublicCheck: isPublic,
        ownsJourney: ownershipData.ownerID === currentUserId
      });
    }
    
    if (isPublic) {
      alert('Public journeys cannot be deleted directly. Please contact support to request deletion of public journeys that are being used by the community.');
      return;
    }

    // Confirm deletion
    const journeyName = journey.title || journey.tripName || journey.name || 'this journey';
    if (!window.confirm(`Are you sure you want to permanently delete "${journeyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };

      // DELETE request to delete the journey
      await axios.delete(`${apiUrl}/journeys/${journeyId}`, { headers });

      // Remove from search results
      setSearchResults(prev => prev.filter(j => (j.journeyID || j.id) !== journeyId));
      
      // Remove from ownership cache
      setJourneyOwnership(prev => {
        const newOwnership = { ...prev };
        delete newOwnership[journeyId];
        return newOwnership;
      });

      alert('Journey deleted successfully.');
    } catch (error) {
      console.error('Error deleting journey:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      
      // Check if error suggests journey is in use
      if (error.response?.status === 400 || error.response?.status === 409 || 
          errorMessage.toLowerCase().includes('in use') || 
          errorMessage.toLowerCase().includes('cannot delete') ||
          errorMessage.toLowerCase().includes('dependency')) {
        alert('This journey cannot be deleted because it is currently in use. Please remove the journey from your explorer board first, or contact support for assistance.');
      } else {
        alert(`Failed to delete journey: ${errorMessage}. Please remove the journey from your explorer board or contact support if the issue persists.`);
      }
    }
  };

  const fetchReviews = async (journeyID) => {
    setIsLoadingReviews(true);
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      // Stub: Replace with actual reviews API endpoint
      // const response = await axios.get(`${apiUrl}/journeys/${journeyID}/reviews`, { headers });
      // setReviews(response.data);
      
      // For now, show empty state
      setReviews([]);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setReviews([]);
    } finally {
      setIsLoadingReviews(false);
    }
  };

  // Questionnaire handlers
  const handleStartQuestionnaire = () => {
    setShowQuestionnaire(true);
    setQuestionnaireStep(0);
    setQuestionnaireAnswers({
      journeyType: '',
      journeyTypeValue: '',
      isFamily: false,
      specialNeeds: '',
      activity: '',
      accessibility: '',
      locationCount: '',
      season: ''
    });
  };

  const handleQuestionnaireNext = () => {
    if (questionnaireStep < 4) {
      setQuestionnaireStep(questionnaireStep + 1);
    } else {
      // Apply filters based on answers
      applyQuestionnaireFilters();
      setShowQuestionnaire(false);
    }
  };

  const handleQuestionnaireBack = () => {
    if (questionnaireStep > 0) {
      setQuestionnaireStep(questionnaireStep - 1);
    }
  };

  const handleQuestionnaireSkip = () => {
    setShowQuestionnaire(false);
  };

  const applyQuestionnaireFilters = () => {
    const newParams = {
      keyword: '',
      ownerID: '',
      scopeLevel: '',
      country: '',
      state: '',
      desires: [],
      feelings: [],
      goals: [],
      recommendedSeason: [],
      sortBy: 'newest',
      locationCountMin: '',
      locationCountMax: ''
    };

    // Map journey type
    if (questionnaireAnswers.journeyType === 'goal' && questionnaireAnswers.journeyTypeValue) {
      newParams.goals = [questionnaireAnswers.journeyTypeValue];
    } else if (questionnaireAnswers.journeyType === 'desire' && questionnaireAnswers.journeyTypeValue) {
      newParams.desires = [questionnaireAnswers.journeyTypeValue];
    } else if (questionnaireAnswers.journeyType === 'feeling' && questionnaireAnswers.journeyTypeValue) {
      newParams.feelings = [questionnaireAnswers.journeyTypeValue];
    }

    // Map season (capitalize first letter to match API format)
    if (questionnaireAnswers.season && questionnaireAnswers.season !== 'any') {
      const seasonCapitalized = questionnaireAnswers.season.charAt(0).toUpperCase() + questionnaireAnswers.season.slice(1);
      newParams.recommendedSeason = [seasonCapitalized];
    }

    // Map location count
    if (questionnaireAnswers.locationCount === 'small') {
      newParams.locationCountMin = '5';
      newParams.locationCountMax = '50';
    } else if (questionnaireAnswers.locationCount === 'medium') {
      newParams.locationCountMin = '51';
      newParams.locationCountMax = '200';
    } else if (questionnaireAnswers.locationCount === 'large') {
      newParams.locationCountMin = '201';
      newParams.locationCountMax = '';
    }

    // Map accessibility (scope level)
    if (questionnaireAnswers.accessibility === 'local') {
      newParams.scopeLevel = 'State'; // Default to state level for local
    }

    setSearchParams(newParams);
  };

  const handleShowReviews = async (journeyID) => {
    setShowReviews(true);
    await fetchReviews(journeyID);
  };

  const handleMultiSelect = (field, value) => {
    setSearchParams(prev => {
      const currentValues = prev[field] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [field]: newValues };
    });
  };

  const toggleFilterExpansion = (filterName) => {
    setExpandedFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="journey-search-modal-overlay" onClick={onClose}>
      <div className="journey-search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Search Journeys</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-content">
          {/* Questionnaire Modal Overlay */}
          {showQuestionnaire && (
            <div className="questionnaire-overlay" onClick={(e) => e.stopPropagation()}>
              <div className="questionnaire-modal">
                <div className="questionnaire-header">
                  <h2>Find Your Perfect Journey</h2>
                  <button className="questionnaire-close-btn" onClick={handleQuestionnaireSkip}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                
                <div className="questionnaire-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${((questionnaireStep + 1) / 5) * 100}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">Question {questionnaireStep + 1} of 5</span>
                </div>

                <div className="questionnaire-content">
                  {/* Question 1: Journey Type */}
                  {questionnaireStep === 0 && (
                    <div className="questionnaire-question">
                      <h3>What's most important for your journey?</h3>
                      <p className="question-subtitle">Choose what matters most to you</p>
                      <div className="question-options">
                        <button
                          className={`question-option ${questionnaireAnswers.journeyType === 'goal' ? 'selected' : ''}`}
                          onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, journeyType: 'goal', journeyTypeValue: '' }))}
                        >
                          <i className="fas fa-bullseye"></i>
                          <span>Goal-Oriented</span>
                          <p>I want to achieve something specific</p>
                        </button>
                        <button
                          className={`question-option ${questionnaireAnswers.journeyType === 'desire' ? 'selected' : ''}`}
                          onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, journeyType: 'desire', journeyTypeValue: '' }))}
                        >
                          <i className="fas fa-heart"></i>
                          <span>Desire-Based</span>
                          <p>I want a specific type of experience</p>
                        </button>
                        <button
                          className={`question-option ${questionnaireAnswers.journeyType === 'feeling' ? 'selected' : ''}`}
                          onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, journeyType: 'feeling', journeyTypeValue: '' }))}
                        >
                          <i className="fas fa-smile"></i>
                          <span>Feeling-Focused</span>
                          <p>I want to feel a certain way</p>
                        </button>
                      </div>
                      
                      {questionnaireAnswers.journeyType && (
                        <div className="question-sub-options">
                          {questionnaireAnswers.journeyType === 'goal' && (
                            <div className="sub-options-grid">
                              {goalOptions.map(goal => (
                                <button
                                  key={goal}
                                  className={`sub-option ${questionnaireAnswers.journeyTypeValue === goal ? 'selected' : ''}`}
                                  onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, journeyTypeValue: goal }))}
                                >
                                  {goal}
                                </button>
                              ))}
                            </div>
                          )}
                          {questionnaireAnswers.journeyType === 'desire' && (
                            <div className="sub-options-grid">
                              {desireOptions.map(desire => (
                                <button
                                  key={desire}
                                  className={`sub-option ${questionnaireAnswers.journeyTypeValue === desire ? 'selected' : ''}`}
                                  onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, journeyTypeValue: desire }))}
                                >
                                  {desire}
                                </button>
                              ))}
                            </div>
                          )}
                          {questionnaireAnswers.journeyType === 'feeling' && (
                            <div className="sub-options-grid">
                              {feelingOptions.map(feeling => (
                                <button
                                  key={feeling}
                                  className={`sub-option ${questionnaireAnswers.journeyTypeValue === feeling ? 'selected' : ''}`}
                                  onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, journeyTypeValue: feeling }))}
                                >
                                  {feeling}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Question 2: Family & Special Needs */}
                  {questionnaireStep === 1 && (
                    <div className="questionnaire-question">
                      <h3>Is this journey for a family?</h3>
                      <p className="question-subtitle">Help us find family-friendly options</p>
                      <div className="question-options">
                        <button
                          className={`question-option ${questionnaireAnswers.isFamily === true ? 'selected' : ''}`}
                          onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, isFamily: true, specialNeeds: '' }))}
                        >
                          <i className="fas fa-users"></i>
                          <span>Yes, for a family</span>
                        </button>
                        <button
                          className={`question-option ${questionnaireAnswers.isFamily === false ? 'selected' : ''}`}
                          onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, isFamily: false, specialNeeds: '' }))}
                        >
                          <i className="fas fa-user"></i>
                          <span>No, just for me</span>
                        </button>
                      </div>
                      
                      {questionnaireAnswers.isFamily === true && (
                        <div className="question-sub-options">
                          <label className="sub-question-label">Any special needs or considerations?</label>
                          <textarea
                            className="sub-option-textarea"
                            placeholder="E.g., wheelchair accessible, kid-friendly activities, pet-friendly..."
                            value={questionnaireAnswers.specialNeeds}
                            onChange={(e) => setQuestionnaireAnswers(prev => ({ ...prev, specialNeeds: e.target.value }))}
                            rows={3}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Question 3: Activity */}
                  {questionnaireStep === 2 && (
                    <div className="questionnaire-question">
                      <h3>Are you looking for a specific activity?</h3>
                      <p className="question-subtitle">Select your primary interest</p>
                      <div className="question-options-grid">
                        <button
                          className={`question-option ${questionnaireAnswers.activity === 'hiking' ? 'selected' : ''}`}
                          onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, activity: 'hiking' }))}
                        >
                          <i className="fas fa-hiking"></i>
                          <span>Hiking</span>
                        </button>
                        <button
                          className={`question-option ${questionnaireAnswers.activity === 'biking' ? 'selected' : ''}`}
                          onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, activity: 'biking' }))}
                        >
                          <i className="fas fa-bicycle"></i>
                          <span>Biking</span>
                        </button>
                        <button
                          className={`question-option ${questionnaireAnswers.activity === 'fishing' ? 'selected' : ''}`}
                          onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, activity: 'fishing' }))}
                        >
                          <i className="fas fa-fish"></i>
                          <span>Fishing</span>
                        </button>
                        <button
                          className={`question-option ${questionnaireAnswers.activity === 'skiing' ? 'selected' : ''}`}
                          onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, activity: 'skiing' }))}
                        >
                          <i className="fas fa-skiing"></i>
                          <span>Skiing</span>
                        </button>
                        <button
                          className={`question-option ${questionnaireAnswers.activity === 'none' ? 'selected' : ''}`}
                          onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, activity: 'none' }))}
                        >
                          <i className="fas fa-ellipsis-h"></i>
                          <span>No specific activity</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Question 4: Accessibility & Location Count */}
                  {questionnaireStep === 3 && (
                    <div className="questionnaire-question">
                      <h3>How accessible should this journey be?</h3>
                      <p className="question-subtitle">Choose your preference for location and journey size</p>
                      <div className="question-options">
                        <button
                          className={`question-option ${questionnaireAnswers.accessibility === 'local' ? 'selected' : ''}`}
                          onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, accessibility: 'local' }))}
                        >
                          <i className="fas fa-map-marker-alt"></i>
                          <span>Local & Close</span>
                          <p>Journeys near my location, easy to access</p>
                        </button>
                        <button
                          className={`question-option ${questionnaireAnswers.accessibility === 'any' ? 'selected' : ''}`}
                          onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, accessibility: 'any' }))}
                        >
                          <i className="fas fa-globe"></i>
                          <span>Anywhere</span>
                          <p>I'm open to journeys anywhere</p>
                        </button>
                      </div>
                      
                      <div className="question-sub-options" style={{ marginTop: 'var(--space-4)' }}>
                        <label className="sub-question-label">How many locations would you like?</label>
                        <div className="question-options-grid">
                          <button
                            className={`question-option ${questionnaireAnswers.locationCount === 'small' ? 'selected' : ''}`}
                            onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, locationCount: 'small' }))}
                          >
                            <span>Small (5-50)</span>
                            <p>Quick and manageable</p>
                          </button>
                          <button
                            className={`question-option ${questionnaireAnswers.locationCount === 'medium' ? 'selected' : ''}`}
                            onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, locationCount: 'medium' }))}
                          >
                            <span>Medium (51-200)</span>
                            <p>Moderate challenge</p>
                          </button>
                          <button
                            className={`question-option ${questionnaireAnswers.locationCount === 'large' ? 'selected' : ''}`}
                            onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, locationCount: 'large' }))}
                          >
                            <span>Large (201+)</span>
                            <p>Epic adventure</p>
                          </button>
                          <button
                            className={`question-option ${questionnaireAnswers.locationCount === 'any' ? 'selected' : ''}`}
                            onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, locationCount: 'any' }))}
                          >
                            <span>Any Size</span>
                            <p>Show me everything</p>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Question 5: Season */}
                  {questionnaireStep === 4 && (
                    <div className="questionnaire-question">
                      <h3>Does time of year matter?</h3>
                      <p className="question-subtitle">When would you like to take this journey?</p>
                      <div className="question-options-grid">
                        {seasonOptions.map(season => (
                          <button
                            key={season}
                            className={`question-option ${questionnaireAnswers.season === season.toLowerCase() ? 'selected' : ''}`}
                            onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, season: season.toLowerCase() }))}
                          >
                            <i className={`fas fa-${season.toLowerCase() === 'spring' ? 'seedling' : season.toLowerCase() === 'summer' ? 'sun' : season.toLowerCase() === 'fall' ? 'leaf' : 'snowflake'}`}></i>
                            <span>{season}</span>
                          </button>
                        ))}
                        <button
                          className={`question-option ${questionnaireAnswers.season === 'any' ? 'selected' : ''}`}
                          onClick={() => setQuestionnaireAnswers(prev => ({ ...prev, season: 'any' }))}
                        >
                          <i className="fas fa-calendar-alt"></i>
                          <span>Any Season</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="questionnaire-actions">
                  <button 
                    className="questionnaire-btn-secondary"
                    onClick={questionnaireStep === 0 ? handleQuestionnaireSkip : handleQuestionnaireBack}
                  >
                    {questionnaireStep === 0 ? 'Skip' : 'Back'}
                  </button>
                  <button 
                    className="questionnaire-btn-primary"
                    onClick={handleQuestionnaireNext}
                    disabled={
                      (questionnaireStep === 0 && (!questionnaireAnswers.journeyType || !questionnaireAnswers.journeyTypeValue)) ||
                      (questionnaireStep === 1 && questionnaireAnswers.isFamily === null) ||
                      (questionnaireStep === 2 && !questionnaireAnswers.activity) ||
                      (questionnaireStep === 3 && (!questionnaireAnswers.accessibility || !questionnaireAnswers.locationCount)) ||
                      (questionnaireStep === 4 && !questionnaireAnswers.season)
                    }
                  >
                    {questionnaireStep === 4 ? 'Find My Journeys' : 'Next'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showDetailsView ? (
            /* Journey Details View */
            <div className="journey-details-view">
              {isLoadingDetails ? (
                <div className="details-loading">
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: '3rem', color: 'var(--text-tertiary)', marginBottom: 'var(--space-4)' }}></i>
                  <p>Loading journey details...</p>
                </div>
              ) : journeyDetails ? (
                <>
              <div className="details-header">
                <h2>{journeyDetails.title}</h2>
                <div className="details-header-actions">
                  <button className="back-button" onClick={handleBackToSearch}>
                    <i className="fas fa-arrow-left"></i>
                    <span>Back to Search</span>
                  </button>
                  {userPlan !== 'Basic' ? (
                    <button 
                      className="header-add-journey-btn" 
                      onClick={handleAddJourney}
                      disabled={isAddingJourney}
                    >
                      {isAddingJourney ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          <span>Adding Journey...</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plus"></i>
                          <span>Add This Journey</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <button 
                      className="header-add-journey-btn" 
                      onClick={() => window.location.href = '/plan-subscription'}
                      style={{ opacity: 0.7 }}
                    >
                      <i className="fas fa-lock"></i>
                      <span>Upgrade to Add Journey</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="details-content-full">
                <div className="details-main-section">
                  <div className="details-section">
                    <h3>Description</h3>
                    <p>{journeyDetails.description}</p>
                  </div>

                  <div className="details-section">
                    <h3>Scope</h3>
                    <p><strong>Level:</strong> {journeyDetails.scope?.level || 'N/A'}</p>
                    {journeyDetails.scope?.countries && journeyDetails.scope.countries.length > 0 && (
                      <p><strong>Countries:</strong> {journeyDetails.scope.countries.join(', ')}</p>
                    )}
                    {journeyDetails.scope?.states && journeyDetails.scope.states.length > 0 && (
                      <p><strong>States:</strong> {journeyDetails.scope.states.join(', ')}</p>
                    )}
                  </div>

                  {journeyDetails.desires && journeyDetails.desires.length > 0 && (
                    <div className="details-section">
                      <h3>Desires</h3>
                      <div className="tag-list">
                        {journeyDetails.desires.map((desire, idx) => (
                          <span key={idx} className="tag">{desire}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {journeyDetails.feelings && journeyDetails.feelings.length > 0 && (
                    <div className="details-section">
                      <h3>Feelings</h3>
                      <div className="tag-list">
                        {journeyDetails.feelings.map((feeling, idx) => (
                          <span key={idx} className="tag">{feeling}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {journeyDetails.goals && journeyDetails.goals.length > 0 && (
                    <div className="details-section">
                      <h3>Goals</h3>
                      <div className="tag-list">
                        {journeyDetails.goals.map((goal, idx) => (
                          <span key={idx} className="tag">{goal}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {journeyDetails.recommendedSeason && journeyDetails.recommendedSeason.length > 0 && (
                    <div className="details-section">
                      <h3>Recommended Season</h3>
                      <div className="tag-list">
                        {journeyDetails.recommendedSeason.map((season, idx) => (
                          <span key={idx} className="tag">{season}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {journeyDetails.overallRating !== undefined && journeyDetails.overallRating !== null && (
                    <div className="details-section">
                      <div className="rating-header">
                        <h3>Rating</h3>
                        {journeyDetails.overallRating > 0 && (
                          <button 
                            className="view-reviews-btn" 
                            onClick={() => handleShowReviews(journeyDetails.journeyID)}
                          >
                            <i className="fas fa-comments"></i>
                            View Reviews
                          </button>
                        )}
                      </div>
                      <p>
                        <i className="fas fa-star"></i> {
                          journeyDetails.overallRating > 0 
                            ? `${journeyDetails.overallRating.toFixed(1)} Overall Rating`
                            : 'No reviews yet'
                        }
                      </p>
                    </div>
                  )}

                  {/* Journey Locations List */}
                  {(() => {
                    const features = getGeoJSONFeatures();
                    
                    if (features.length === 0) {
                      return (
                        <div className="details-section">
                          <h3>Journey Locations</h3>
                          <p>No location data available for this journey.</p>
                        </div>
                      );
                    }
                    
                    // Sort features alphabetically by location name
                    const sortedFeatures = [...features].sort((a, b) => {
                      const nameA = (a.name || a.properties?.name || '').toLowerCase();
                      const nameB = (b.name || b.properties?.name || '').toLowerCase();
                      return nameA.localeCompare(nameB);
                    });
                    
                    return (
                      <div className="details-section">
                        <h3>Journey Locations</h3>
                        <p className="features-summary">
                          <strong>Total Locations:</strong> {journeyDetails.locationCount !== undefined ? journeyDetails.locationCount : features.length}
                        </p>
                        <ul className="locations-list">
                          {sortedFeatures.map((feature, idx) => {
                            // Handle both GeoJSON format and object format
                            const locationName = feature.name || feature.properties?.name || `Location ${idx + 1}`;
                            
                            return (
                              <li key={idx} className="location-name-item">
                                {locationName}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })()}

                </div>

                <div className="details-actions">
                  <button className="back-to-search-btn" onClick={handleBackToSearch}>
                    <i className="fas fa-arrow-left"></i>
                    Back to Search
                  </button>
                  {userPlan !== 'Basic' ? (
                    <button 
                      className="add-journey-btn" 
                      onClick={handleAddJourney}
                      disabled={isAddingJourney}
                    >
                      {isAddingJourney ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Adding Journey...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plus"></i>
                          Add This Journey
                        </>
                      )}
                    </button>
                  ) : (
                    <button 
                      className="add-journey-btn" 
                      onClick={() => window.location.href = '/plan-subscription'}
                      style={{ opacity: 0.7 }}
                    >
                      <i className="fas fa-lock"></i>
                      Upgrade to Add Journey
                    </button>
                  )}
                </div>
              </div>
                </>
              ) : (
                <div className="details-error">
                  <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: 'var(--text-tertiary)', marginBottom: 'var(--space-4)' }}></i>
                  <p>Failed to load journey details.</p>
                  <button className="back-button" onClick={handleBackToSearch}>
                    <i className="fas fa-arrow-left"></i>
                    <span>Back to Search</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Search View */
            <>
              <div className="results-header">
                <div className="results-summary">
                  {searchResults.length > 0 && (
                    <span>
                      Showing {searchResults.length} {searchResults.length === 1 ? 'journey' : 'journeys'}
                      {hasMorePages && ' (more available)'}
                    </span>
                  )}
                </div>
                <div className="sort-control">
                  <label>Sort by:</label>
                  <select
                    className="sort-select"
                    value={searchParams.sortBy}
                    onChange={(e) => setSearchParams(prev => ({ ...prev, sortBy: e.target.value }))}
                  >
                    {sortOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="main-content-layout">
            {/* Left Sidebar Filters */}
            <div className="filters-sidebar">
              <div className="filter-section">
                <button 
                  className="questionnaire-btn"
                  onClick={handleStartQuestionnaire}
                >
                  <i className="fas fa-question-circle"></i>
                  <span>Find My Perfect Journey</span>
                </button>
              </div>
              <div className="filter-section">
                <h3 className="filter-section-title">Search</h3>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Keyword search..."
                  value={searchParams.keyword}
                  onChange={(e) => setSearchParams(prev => ({ ...prev, keyword: e.target.value }))}
                />
              </div>

              <div className="filter-section">
                <h3 className="filter-section-title">Owner</h3>
                <select
                  className="filter-select"
                  value={searchParams.ownerID}
                  onChange={(e) => setSearchParams(prev => ({ ...prev, ownerID: e.target.value }))}
                >
                  <option value="">All Owners</option>
                  {currentUserId && (
                    <option key={currentUserId} value={currentUserId}>My Journeys</option>
                  )}
                  {ownerOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="filter-section">
                <h3 className="filter-section-title">Scope Level</h3>
                <select
                  className="filter-select"
                  value={searchParams.scopeLevel}
                  onChange={(e) => setSearchParams(prev => ({ ...prev, scopeLevel: e.target.value }))}
                >
                  <option value="">All Scopes</option>
                  {scopeLevelOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="filter-section">
                <h3 className="filter-section-title">Location</h3>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Country"
                  value={searchParams.country}
                  onChange={(e) => setSearchParams(prev => ({ ...prev, country: e.target.value }))}
                />
                <input
                  type="text"
                  className="filter-input"
                  placeholder="State"
                  value={searchParams.state}
                  onChange={(e) => setSearchParams(prev => ({ ...prev, state: e.target.value }))}
                  style={{ marginTop: 'var(--space-2)' }}
                />
              </div>

              <div className="filter-section">
                <div className="filter-section-header" onClick={() => toggleFilterExpansion('desires')}>
                  <h3 className="filter-section-title">Desires</h3>
                  <i className={`fas fa-chevron-${expandedFilters.desires ? 'up' : 'down'}`}></i>
                </div>
                {expandedFilters.desires && (
                  <div className="filter-checkboxes">
                    {desireOptions.map(desire => (
                      <label key={desire} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={searchParams.desires.includes(desire)}
                          onChange={() => handleMultiSelect('desires', desire)}
                        />
                        <span>{desire}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="filter-section">
                <div className="filter-section-header" onClick={() => toggleFilterExpansion('feelings')}>
                  <h3 className="filter-section-title">Feelings</h3>
                  <i className={`fas fa-chevron-${expandedFilters.feelings ? 'up' : 'down'}`}></i>
                </div>
                {expandedFilters.feelings && (
                  <div className="filter-checkboxes">
                    {feelingOptions.map(feeling => (
                      <label key={feeling} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={searchParams.feelings.includes(feeling)}
                          onChange={() => handleMultiSelect('feelings', feeling)}
                        />
                        <span>{feeling}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="filter-section">
                <div className="filter-section-header" onClick={() => toggleFilterExpansion('goals')}>
                  <h3 className="filter-section-title">Goals</h3>
                  <i className={`fas fa-chevron-${expandedFilters.goals ? 'up' : 'down'}`}></i>
                </div>
                {expandedFilters.goals && (
                  <div className="filter-checkboxes">
                    {goalOptions.map(goal => (
                      <label key={goal} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={searchParams.goals.includes(goal)}
                          onChange={() => handleMultiSelect('goals', goal)}
                        />
                        <span>{goal}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="filter-section">
                <div className="filter-section-header" onClick={() => toggleFilterExpansion('season')}>
                  <h3 className="filter-section-title">Recommended Season</h3>
                  <i className={`fas fa-chevron-${expandedFilters.season ? 'up' : 'down'}`}></i>
                </div>
                {expandedFilters.season && (
                  <div className="filter-checkboxes">
                    {seasonOptions.map(season => (
                      <label key={season} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={searchParams.recommendedSeason.includes(season)}
                          onChange={() => handleMultiSelect('recommendedSeason', season)}
                        />
                        <span>{season}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="filter-section">
                <h3 className="filter-section-title">Location Count</h3>
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                  <input
                    type="number"
                    className="filter-input"
                    placeholder="Min"
                    value={searchParams.locationCountMin}
                    onChange={(e) => setSearchParams(prev => ({ ...prev, locationCountMin: e.target.value }))}
                    style={{ flex: 1 }}
                    min="0"
                  />
                  <span style={{ color: 'var(--text-secondary)' }}>to</span>
                  <input
                    type="number"
                    className="filter-input"
                    placeholder="Max"
                    value={searchParams.locationCountMax}
                    onChange={(e) => setSearchParams(prev => ({ ...prev, locationCountMax: e.target.value }))}
                    style={{ flex: 1 }}
                    min="0"
                  />
                </div>
              </div>

              <div className="filter-section">
                <button className="clear-filters-btn" onClick={() => {
                  setSearchParams({
                    keyword: '',
                    ownerID: '',
                    scopeLevel: '',
                    country: '',
                    state: '',
                    desires: [],
                    feelings: [],
                    goals: [],
                    recommendedSeason: [],
                    sortBy: 'newest',
                    locationCountMin: '',
                    locationCountMax: ''
                  });
                }}>
                  Clear All Filters
                </button>
              </div>
            </div>

            {/* Main Results Area Container */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              flex: '1 1 0%',
              overflow: 'hidden', 
              minHeight: 0,
              maxHeight: '100%'
            }}>
            <div className={`search-results-main ${userPlan === 'Basic' && searchResults.length > 3 ? 'basic-plan-blur' : ''}`} style={{ flex: '1 1 auto', overflowY: 'auto', overflowX: 'hidden' }}>
              {isLoading && searchResults.length === 0 && (
                <div className="empty-results">
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: '3rem', color: 'var(--text-tertiary)', marginBottom: 'var(--space-4)' }}></i>
                  <p>Loading journeys...</p>
                </div>
              )}
              {!isLoading && searchResults.length === 0 && (
                <div className="empty-results">
                  <i className="fas fa-search" style={{ fontSize: '3rem', color: 'var(--text-tertiary)', marginBottom: 'var(--space-4)' }}></i>
                  <h3>No journeys found</h3>
                  <p>Try adjusting your filters to see more results.</p>
                </div>
              )}

              {(userPlan === 'Basic' ? searchResults.slice(0, 3) : searchResults).map((journey, index) => {
              const isInCompare = journeysToCompare.some(j => j.journeyID === journey.journeyID);
              const features = getGeoJSONFeatures(journey);
              const locationCount = journey.locationCount !== undefined ? journey.locationCount : features.length;
              
              return (
                <div
                  key={`search-${journey.journeyID}-${index}`}
                  className={`journey-result-card ${selectedJourney?.journeyID === journey.journeyID ? 'selected' : ''}`}
                  onClick={() => handleJourneyClick(journey)}
                >
                  <div className="journey-result-header">
                    <h3 style={{ cursor: 'pointer' }}>
                      {journey.title}
                    </h3>
                    <div className="journey-meta">
                      <span className="owner-badge">{journey.ownerID === 'System' ? 'System' : 'Community'}</span>
                      {locationCount > 0 && (
                        <span className="location-count-badge">
                          <i className="fas fa-map-marker-alt"></i> {locationCount}
                        </span>
                      )}
                      {journey.overallRating !== undefined && journey.overallRating !== null ? (
                        journey.overallRating > 0 ? (
                          <span 
                            className="rating-badge clickable"
                            onClick={() => handleShowReviews(journey.journeyID)}
                            title="View Reviews"
                          >
                            <i className="fas fa-star"></i> {journey.overallRating.toFixed(1)}
                          </span>
                        ) : (
                          <span 
                            className="rating-badge"
                            title="No reviews yet"
                            style={{ opacity: 0.7, cursor: 'default' }}
                          >
                            <i className="fas fa-star" style={{ opacity: 0.5 }}></i> No reviews yet
                          </span>
                        )
                      ) : null}
                    </div>
                  </div>
                  <p className="journey-description">{journey.description}</p>
                  
                  <div className="journey-tags">
                    {journey.scope?.level && (
                      <span className="tag">Scope: {journey.scope.level}</span>
                    )}
                    {journey.desires?.length > 0 && (
                      <span className="tag">Desires: {journey.desires.join(', ')}</span>
                    )}
                    {journey.feelings?.length > 0 && (
                      <span className="tag">Feelings: {journey.feelings.join(', ')}</span>
                    )}
                    {journey.goals?.length > 0 && (
                      <span className="tag">Goals: {journey.goals.join(', ')}</span>
                    )}
                  </div>
                  
                  <div className="journey-card-actions">
                    {userPlan !== 'Basic' ? (
                      <button
                        className="quick-add-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuickAdd(journey);
                        }}
                        title="Quick Add Journey"
                        disabled={addingJourneyId === journey.journeyID}
                      >
                        {addingJourneyId === journey.journeyID ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            Adding...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-plus"></i>
                            Quick Add
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        className="quick-add-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = '/plan-subscription';
                        }}
                        title="Upgrade to Add Journey"
                        style={{ opacity: 0.7 }}
                      >
                        <i className="fas fa-lock"></i>
                        Upgrade
                      </button>
                    )}
                    {userPlan !== 'Basic' ? (
                      <button
                        className={`compare-btn ${isInCompare ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleCompare(journey);
                        }}
                        title={isInCompare ? 'Remove from Compare' : 'Add to Compare'}
                      >
                        <i className={`fas ${isInCompare ? 'fa-check-square' : 'fa-square'}`}></i>
                        {isInCompare ? 'Comparing' : 'Compare'}
                      </button>
                    ) : (
                      <button
                        className="compare-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = '/plan-subscription';
                        }}
                        title="Upgrade to Compare Journeys"
                        style={{ opacity: 0.7 }}
                      >
                        <i className="fas fa-lock"></i>
                        Upgrade
                      </button>
                    )}
                    <button
                      className="view-details-btn"
                      onClick={() => handleJourneyClick(journey)}
                      title="View Full Details"
                    >
                      <i className="fas fa-info-circle"></i>
                      Details
                    </button>
                    
                    {/* Delete button - only show if user owns the journey */}
                    {(() => {
                      const journeyId = journey.journeyID || journey.id;
                      const ownership = journeyOwnership[journeyId];
                      const isOwner = ownership && ownership.ownerID === currentUserId;
                      
                      if (!isOwner) return null;
                      
                      return (
                        <button
                          className="delete-btn"
                          onClick={(e) => handleDeleteJourney(e, journey)}
                          title="Delete Journey Permanently (Only for private journeys you own)"
                          style={{ 
                            color: '#dc3545', 
                            backgroundColor: 'transparent',
                            border: '1px solid #dc3545',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <i className="fas fa-trash-alt"></i>
                          Delete
                        </button>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
            </div>

            {/* Basic Plan Upgrade Message */}
            {userPlan === 'Basic' && searchResults.length > 3 && (
              <div style={{ 
                padding: 'var(--space-4)', 
                borderTop: '1px solid var(--border-primary)',
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                width: '100%',
                background: 'var(--bg-primary)',
                flexShrink: 0,
                flexGrow: 0,
                minHeight: 'fit-content',
                boxSizing: 'border-box'
              }}>
                <div className="basic-plan-upgrade-message">
                  <p>Upgrade to start a journey and add them to your Explorer Board!</p>
                  <button onClick={() => window.location.href = '/plan-subscription'}>
                    Upgrade Now
                  </button>
                </div>
              </div>
            )}

            {/* Load More Button - Outside scrollable container */}
            {hasMorePages && userPlan !== 'Basic' && (
              <div style={{ 
                padding: 'var(--space-4)', 
                borderTop: '1px solid var(--border-primary)',
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                width: '100%',
                background: 'var(--bg-primary)',
                flexShrink: 0,
                flexGrow: 0,
                minHeight: 'fit-content'
              }}>
                <button 
                  className="load-more-btn" 
                  onClick={handleLoadMore} 
                  disabled={isLoading}
                  style={{ minWidth: '200px' }}
                >
                  {isLoading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
            </div>
              </div>
            </>
          )}

          {/* Compare Journeys Panel */}
          {journeysToCompare.length > 0 && (
            <div className="compare-panel">
              <div className="compare-header">
                <h3>Comparing {journeysToCompare.length} Journey{journeysToCompare.length !== 1 ? 's' : ''}</h3>
                <button className="compare-close-btn" onClick={() => setJourneysToCompare([])}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="compare-content">
                {journeysToCompare.map((journey, index) => {
                  return (
                    <div key={`compare-${journey.journeyID}-${index}`} className="compare-item">
                      <h4>{journey.title}</h4>
                      <p className="compare-description">{journey.description}</p>
                      <div className="compare-stats">
                        <span>
                          <i className="fas fa-star"></i> {
                            journey.overallRating !== undefined && journey.overallRating !== null
                              ? (journey.overallRating > 0 ? journey.overallRating.toFixed(1) : 'No reviews yet')
                              : 'N/A'
                          }
                        </span>
                        <span><i className="fas fa-map-marker-alt"></i> {journey.locationCount !== undefined ? journey.locationCount : 'N/A'} locations</span>
                        <span><i className="fas fa-globe"></i> {journey.scope?.level || 'N/A'}</span>
                      </div>
                      <button
                        className="compare-add-btn"
                        onClick={() => handleQuickAdd(journey)}
                        disabled={addingJourneyId === journey.journeyID}
                      >
                        {addingJourneyId === journey.journeyID ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            Adding...
                          </>
                        ) : (
                          'Add Journey'
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="compare-actions">
                {userPlan !== 'Basic' ? (
                  <button 
                    className="compare-add-all-btn" 
                    onClick={async () => {
                      for (const journey of journeysToCompare) {
                        await handleQuickAdd(journey);
                      }
                      setJourneysToCompare([]);
                    }}
                    disabled={addingJourneyId !== null}
                  >
                    {addingJourneyId !== null ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Adding Journeys...
                      </>
                    ) : (
                      'Add All Journeys'
                    )}
                  </button>
                ) : (
                  <button 
                    className="compare-add-all-btn" 
                    onClick={() => window.location.href = '/plan-subscription'}
                    style={{ opacity: 0.7 }}
                  >
                    Upgrade to Add All
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Reviews Modal */}
          {showReviews && (
            <div className="reviews-modal-overlay" onClick={() => setShowReviews(false)}>
              <div className="reviews-modal" onClick={(e) => e.stopPropagation()}>
                <div className="reviews-modal-header">
                  <h3>Reviews</h3>
                  <button className="modal-close-btn" onClick={() => setShowReviews(false)}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="reviews-modal-content">
                  {isLoadingReviews ? (
                    <div className="reviews-loading">
                      <i className="fas fa-spinner fa-spin"></i>
                      <p>Loading reviews...</p>
                    </div>
                  ) : reviews.length === 0 ? (
                    <div className="reviews-empty">
                      <i className="fas fa-comments"></i>
                      <p>No reviews yet. Be the first to review this journey!</p>
                    </div>
                  ) : (
                    <div className="reviews-list">
                      {reviews.map((review, idx) => (
                        <div key={idx} className="review-item">
                          <div className="review-header">
                            <span className="review-author">{review.author || 'Anonymous'}</span>
                            <span className="review-rating">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <i
                                  key={i}
                                  className={`fas fa-star ${i < (review.rating || 0) ? 'filled' : ''}`}
                                ></i>
                              ))}
                            </span>
                          </div>
                          <p className="review-text">{review.text}</p>
                          <span className="review-date">{review.date || ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JourneySearchModal;
